/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { Close } from '@ahamove/icons';
import { shadow } from '@ahamove/tokens';
import {
  DeliveryType,
  batchGeocodeFn,
  getAddressLabels,
  type Address,
  type DefaultQueryError,
  type Path,
} from '@ahm/api-wrappers-core';
import { validatePhoneNumberForLogin } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { useLocationStore, useMapControl } from '@ahm/react-map/store';
import { FloatingLabelInput, toast } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Group,
  Modal,
  ScrollArea,
  Skeleton,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { closeAllModals, closeModal, openContextModal } from '@mantine/modals';
import { X as IconX } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useWatch,
  type SubmitHandler,
} from 'react-hook-form';
import { z } from 'zod';
import AddressLevel from './adress-level';
import LocationFeaturesSelector from './location-features-selector';
import ModalAddBankAccount from './modal-add-bank-account';
import { ModalMaxCodInfoDetail } from './modal-max-cod-info-detail';
import NationwidePathCod from './nationwide-path-cod';
import PathCod from './path-cod';
import PathItemValue from './path-item-value';
import PathRecipientInfo from './path-recipient-info';
import PickAddressDetails from './pick-address-details';
import IconMinusCircle from '@/assets/icons/minus-circle-icon.svg';
import MapContainer from '@/components/map-container';
import { ModalName } from '@/components/modals';
import CustomComponent from '@/components/modals/modal-custom-drawer';
import ModalMapAutocomplete from '@/components/modals/modal-map-autocomplete';
import { ROUTES } from '@/config/router';
import { useGetLocationFeatures } from '@/features/booking/api/service/use-get-location-features';
import { ActionIndexPath } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { fetchLocationInfo, getServiceType } from '@/lib/utils';
import { useDetectModalStore } from '@/stores';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type FormPathProps = {
  path?: Path;
  onConfirm: (path: Path) => void;
  selectedAddress?: Address;
  pathIndex: number;
  onClose?: () => void;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const FormPath: React.FC<FormPathProps> = (props) => {
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const { pathIndex, path, onConfirm, selectedAddress, onClose } = props;
  const [openedAutocomplete, autocompleteActions] = useDisclosure(false);
  const addToFrequentLocations = useLocationStore.use.addToFrequentLocations();
  const {
    selectedService,
    statusShowedAddBankProfit,
    setStatusShowedAddBankProfit,
    setSelectedMapAddress,
    clearSelectedMapAddress,
  } = useBookingStore((state) => state);

  const nearbyEntrances = useMapControl((state) => state.nearbyEntrances);

  const tBooking = useTranslations('booking');
  // const tActions = useTranslations('common.actions');
  const tCommon = useTranslations('common');
  const tAuth = useTranslations('system.auth');
  const [addressEdited, setAddressEdited] = useState<string | null>(null);
  const { session } = useAuth();
  const [geocodeLoading, setGeocodeLoading] = useState<boolean>(false);

  const user = session?.user;

  const codInputRef = useRef<HTMLInputElement>(null);

  const setDetectModalId = useDetectModalStore.use.setDetectModalId();
  const clearDetectModalId = useDetectModalStore.use.clearDetectModalId();

  const [isOpenCustomModal, setIsOpenCustomModal] = useState(false);
  const [currentModalId, setCurrentModalId] = useState('');

  const { isNationwide, isTruckService, isBikeService, isHMService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const { data: locationFeatures } = useGetLocationFeatures({
    enabled: isHMService,
  });

  const handleOpenModal = (modalId) => {
    const hasUserBankInfo = Boolean(user?.wallet_info?.bank?.account_id);
    if (modalId === 'cod-add-bank-account') {
      if (!statusShowedAddBankProfit && !hasUserBankInfo) {
        setDetectModalId(modalId);
        setCurrentModalId(modalId);
        setIsOpenCustomModal(true);
      }
    } else {
      setIsOpenCustomModal(true);
      setDetectModalId(modalId);
      setCurrentModalId(modalId);
    }
  };

  const handleCloseCustomModal = (confirm: boolean) => {
    if (currentModalId === 'cod-add-bank-account') {
      onCloseAddBankModal(currentModalId, confirm);
    }
    setIsOpenCustomModal(false);
    setCurrentModalId('');
    clearDetectModalId();
  };
  const renderModalContent = () => {
    if (currentModalId === 'cod-add-bank-account') {
      return <ModalAddBankAccount onClose={handleCloseCustomModal} />;
    }

    if (currentModalId === 'max-cod') {
      return (
        <ModalMaxCodInfoDetail onClose={() => handleCloseCustomModal(false)} />
      );
    }

    return null;
  };

  const pickupSchema = z.object({
    name: z.string().optional(),
    mobile: z
      .string()
      .refine((value) => validatePhoneNumberForLogin(value), {
        message: tAuth('invalidPhoneNumber'),
      })
      .optional()
      .or(z.literal('')),
    remarks: z.string().optional(),
    level: z.string().optional(),
    location_features: z.array(z.string()).optional(),
  });

  const dropoffSchema = z.object({
    name: z
      .string({
        required_error: 'Vui lòng nhập tên',
        invalid_type_error: 'Vui lòng nhập tên',
      })
      .min(2, { message: 'Vui lòng nhập ít nhất 2 ký tự' }),
    mobile: z
      .string({
        required_error: 'Vui lòng nhập sdt',
        invalid_type_error: 'Vui lòng nhập sdt',
      })
      .refine((value) => validatePhoneNumberForLogin(value), {
        message: tAuth('invalidPhoneNumber'),
      })
      .refine((value) => value !== '', {
        message: 'Vui lòng nhập sdt',
      }),
    remarks: z.string().optional(),
    first_label: z
      .string({
        required_error: tBooking('location.admin.error.please_select_street'),
        invalid_type_error: tBooking(
          'location.admin.error.please_select_street'
        ),
      })
      .refine((value) => value !== '', {
        message: tBooking('location.admin.error.please_select_street'),
      }),
    tracking_number: z.string().optional(),
    cod: z.number().optional(),
    item_value: z.number().optional(),
    inspection_policies: z.array(z.string()).optional(),
    level: z.string().optional(),
    location_features: z.array(z.string()).optional(),
  });

  const dropoffNationwideSchema = dropoffSchema.extend({
    name: z
      .string({
        required_error: 'Vui lòng nhập tên',
        invalid_type_error: 'Vui lòng nhập tên',
      })
      .min(2, { message: 'Vui lòng nhập ít nhất 2 ký tự' }),
    mobile: z
      .string({
        required_error: 'Vui lòng nhập sdt',
        invalid_type_error: 'Vui lòng nhập sdt',
      })
      .refine((value) => validatePhoneNumberForLogin(value), {
        message: tAuth('invalidPhoneNumber'),
      })
      .refine((value) => value !== '', {
        message: 'Vui lòng nhập sdt',
      }),
    nationwide_province_id: z
      .string({
        required_error: tBooking('location.admin.error.please_select_province'),
        invalid_type_error: tBooking(
          'location.admin.error.please_select_province'
        ),
      })
      .min(1, {
        message: tBooking('location.admin.error.please_select_province'),
      }),
    nationwide_district_id: z
      .string({
        required_error: tBooking('location.admin.error.please_select_district'),
        invalid_type_error: tBooking(
          'location.admin.error.please_select_district'
        ),
      })
      .min(1, {
        message: tBooking('location.admin.error.please_select_district'),
      }),
    nationwide_ward_id: z
      .string({
        required_error: tBooking('location.admin.error.please_select_ward'),
        invalid_type_error: tBooking('location.admin.error.please_select_ward'),
      })
      .min(1, {
        message: tBooking('location.admin.error.please_select_ward'),
      }),
  });

  const handleOpenModalAddress = () => {
    setDetectModalId('MapAutoComplete');
    autocompleteActions.open();
  };

  const pathSchema = useMemo(() => {
    return pathIndex === ActionIndexPath.Pickup
      ? pickupSchema
      : pathname === ROUTES.nationwide
        ? dropoffNationwideSchema
        : dropoffSchema;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathIndex]);

  const form = useForm<Path>({
    resolver: zodResolver(pathSchema),
    mode: 'onChange',
    defaultValues: { ...path, item_value: 0 },
    reValidateMode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
  } = form;

  const handleCancel = () => {
    typeof onClose === 'function' && onClose();
  };

  const onSubmit: SubmitHandler<Path> = async (data) => {
    if (addressEdited && pathIndex !== ActionIndexPath.Pickup && isNationwide) {
      await handleAddressEdited(data);
    } else {
      const newData = await handleDefaultData(data);
      onConfirm(newData);
      closeAllModals();
    }
  };

  const streetDetails = useWatch({
    name: 'first_label',
    control,
  });

  const [debouncedStreetDetails] = useDebouncedValue(streetDetails, 100);

  const handleAddressEdited = async (data: Path): Promise<void> => {
    try {
      setGeocodeLoading(true);
      if (addressEdited) {
        const res = await batchGeocodeFn({
          addresses: [{ address: addressEdited }],
        });
        const [result] = res;

        if (!result.latitude || !result.longitude) {
          toast.error({
            title: 'Không tìm thấy địa chỉ.',
            message: 'Vui lòng thử lại.',
            withIcon: true,
          });

          return;
        }

        const [first_label, second_label] = getAddressLabels(
          result.address ?? ''
        );

        onConfirm({
          ...data,
          address: result.address,
          lat: result.latitude,
          lng: result.longitude,
          first_label,
          second_label,
          adr_source: result.source || '',
          location_features: result.location_features || [],
        } as Path);
        closeAllModals();
      }
    } catch (error) {
      const errorData = (error as DefaultQueryError).response?.data;
      toast.error({
        title: errorData?.title,
        message: errorData?.message,
        withIcon: true,
      });

      console.error('Error getting batch geocode:', error);
    } finally {
      setGeocodeLoading(false);
    }
  };

  const handleDefaultData = (data: Path): Path => {
    let firstLabel = debouncedStreetDetails;

    if (!debouncedStreetDetails) {
      firstLabel = selectedAddress?.address
        ? getAddressLabels(selectedAddress.address)[0]
        : path?.address
          ? getAddressLabels(path.address)[0]
          : '';
    }

    return {
      ...data,
      lat: selectedAddress?.lat || path?.lat || 0,
      lng: selectedAddress?.lng || path?.lng || 0,
      address: selectedAddress?.address || path?.address || '',
      first_label: firstLabel,
      second_label: selectedAddress?.address
        ? getAddressLabels(selectedAddress.address)[1]
        : path?.address
          ? getAddressLabels(path.address)[1]
          : '',
      adr_source: selectedAddress?.adr_source || path?.adr_source || '',
      entrances: selectedAddress?.entrances || path?.entrances || [],
      polygon: selectedAddress?.polygon || path?.polygon || null,
      location_features:
        selectedAddress?.location_features || path?.location_features || [],
    } as Path;
  };

  const handleInputChange = (
    field: keyof Path,
    inputValue: string | number
  ) => {
    let value: number | undefined;

    if (typeof inputValue === 'string') {
      value = Number(inputValue.replace(/^0+/, '')) || 0;
    } else {
      value = inputValue === 0 ? 0 : Number(inputValue);
    }

    setValue(field, value);
  };

  const centerCoords = useMemo(() => {
    const values = getValues();

    if (selectedAddress?.lat && selectedAddress.lng) {
      return {
        latitude: selectedAddress.lat,
        longitude: selectedAddress.lng,
      };
    }

    return values.lat && values.lng
      ? {
          latitude: values.lat,
          longitude: values.lng,
        }
      : null;
  }, [getValues, selectedAddress]);

  const handleCloseAddressModal = () => {
    autocompleteActions.close();
    clearDetectModalId();

    // Clear selected map address when closing
    clearSelectedMapAddress();

    if (selectedAddress) {
      // Case btn Back
      openContextModal({
        id: ModalName.Path,
        modal: ModalName.Path,
        innerProps: {
          pathIndex,
          path,
          selectedAddress,
          onConfirm,
        },
        fullScreen: true,
        withCloseButton: false,
        scrollAreaComponent: ScrollArea.Autosize,
        classNames: {
          content:
            '[&>div]:w-full [&>div>div]:w-full [&_.ahm-ScrollArea-viewport>div]:!block',
        },
        styles: {
          content: {
            backgroundColor: 'var(--mantine-color-neutral-0)',
          },
          body: {
            position: 'relative',
            padding: 0,
          },
        },
      });
    }

    if (!path && !selectedAddress) {
      closeAllModals();
    }
  };

  const handleChangeAddress = async (address: Address | null) => {
    if (!address?.lat || !address?.lng) return;

    try {
      const commonAddressProps = {
        ...address,
        name: address?.name || address?.contact_name || '',
        mobile: address?.mobile || address?.contact_number || '',
        level: address?.level,
        remarks: address?.remarks || address?.address_detail || '',
      };

      let newAddress;
      if (isNationwide) {
        const { provinceInfo, districtInfo, wardInfo } =
          await fetchLocationInfo(Number(address.lat), Number(address.lng));

        newAddress = {
          ...commonAddressProps,
          nationwide_province_id: provinceInfo?._id,
          nationwide_district_id: districtInfo?._id,
          nationwide_ward_id: wardInfo?._id,
        };
      } else if (isHMService) {
        newAddress = {
          ...commonAddressProps,
          location_features:
            address.location_features || locationFeatures?.[0]?.code
              ? [locationFeatures[0].code]
              : [],
        };
      } else {
        newAddress = commonAddressProps;
      }
      console.log('newAddress', newAddress);
      reset((prev) => ({ ...prev, ...newAddress }));

      if (newAddress.second_label) setAddressEdited(newAddress.second_label);

      void addToFrequentLocations(address);
      handleCloseAddressModal();

      setSelectedMapAddress(newAddress);

      openContextModal({
        id: ModalName.Path,
        modal: ModalName.Path,
        innerProps: {
          pathIndex,
          path,
          selectedAddress: newAddress,
          onConfirm,
        },
        fullScreen: true,
        withCloseButton: false,
        scrollAreaComponent: ScrollArea.Autosize,
        classNames: {
          content:
            '[&>div]:w-full [&>div>div]:w-full [&_.ahm-ScrollArea-viewport>div]:!block',
        },
        styles: {
          content: {
            backgroundColor: 'var(--mantine-color-neutral-0)',
          },
          body: {
            position: 'relative',
            padding: 0,
          },
        },
      });
    } catch (error) {
      console.error('Error in handleChangeAddress:', error);
    }
  };

  const handleOpenAddressModal = () => {
    // closeModal(ModalName.Path);
    // reset();

    handleOpenModalAddress();
  };

  const isHideItemValue = useMemo(() => {
    switch (pathname) {
      case ROUTES.nationwide:
        return true;
      case ROUTES.home:
        return false;
      default:
        return false;
    }
  }, [pathname]);

  const onCloseAddBankModal = (id = '', isConfirm = false) => {
    if (isConfirm) {
      const queryParams = {
        showAddBank: 'true',
      };

      const balanceUrl = `${process.env.NEXT_PUBLIC_APP_V1_URI}/balance?${new URLSearchParams(queryParams).toString()}`;

      window.location.href = balanceUrl;
    }

    setStatusShowedAddBankProfit(true);
    codInputRef.current?.focus();
    closeModal(id);
    clearDetectModalId();
  };

  useEffect(() => {
    if (selectedAddress) {
      void handleChangeAddress(selectedAddress);
    } else {
      if (path) {
        if (path?.second_label) setAddressEdited(path.second_label);

        reset((prev) => ({ ...prev, ...path, level: path.level }));
      } else {
        if (!getValues('lat') || !getValues('lng')) handleOpenAddressModal();
      }
    }
    return () => {
      reset();
      //  handleSelectSlide(ADDRESS_LEVELS[0].value);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress?.lat, selectedAddress?.lng, path]);

  useEffect(() => {
    if (path && !isMobile) reset(path);
  }, [isMobile, path, reset]);

  useEffect(() => {
    if (isHMService && locationFeatures && locationFeatures.length > 0) {
      const currentValue = getValues('location_features' as any);
      if (!currentValue || currentValue.length === 0) {
        setValue('location_features' as any, [locationFeatures[0].code]);
      }
    }
  }, [locationFeatures, isHMService, setValue, getValues]);

  const isShowAdminAreas = useMemo(() => {
    return Boolean(
      pathIndex !== ActionIndexPath.Pickup && pathname === ROUTES.nationwide
      // && selectedService?.delivery_type === DeliveryType.Nationwide
    );
  }, [pathIndex, pathname]);

  const mainEntrance = useMemo(() => {
    return (
      nearbyEntrances?.find(
        (entrance) =>
          !entrance.enabled &&
          entrance.coordinates[0] === getValues('lat') &&
          entrance.coordinates[1] === getValues('lng')
      ) || {}
    );
  }, [nearbyEntrances, getValues('lat'), getValues('lng')]);

  if (isMobile)
    return (
      <>
        <CustomComponent
          modalId={currentModalId}
          isOpen={isOpenCustomModal}
          onClose={() => handleCloseCustomModal(false)}
          optionStyle={{
            headerStyle: {
              minHeight: rem(45),
              padding: 0,
            },
            bodyStyle: {
              paddingTop: 0,
            },
            contentStyle: {
              borderRadius: 0,
              height: '100%',
            },
          }}
          closeButtonProps={{
            pos: 'absolute',
            left: rem(12),
            top: rem(10),
          }}
        >
          {renderModalContent()}
        </CustomComponent>

        <Group
          pos="sticky"
          top={0}
          left={0}
          justify="space-between"
          w="100%"
          px={6}
          bg="white"
          styles={{
            root: {
              zIndex: 100,
              borderBottom: `1px solid var(--mantine-color-neutral-1)`,
            },
          }}
        >
          <ActionIcon
            size={rem(44)}
            variant="transparent"
            onClick={() => closeAllModals()}
            c="neutral.6"
            style={{
              outline: 'none',
            }}
          >
            <IconX width={24} height={24} />
          </ActionIcon>
          <Title order={4}>
            {tBooking(
              pathIndex === ActionIndexPath.Pickup
                ? 'location.pickup_details'
                : 'location.delivery_details'
            )}
          </Title>
          <Space w={44} />
        </Group>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {centerCoords?.latitude && centerCoords?.longitude ? (
              <Box className="sticky left-0 top-0" h="50vh">
                <MapContainer
                  centerCoords={centerCoords}
                  isPickup={Boolean(pathIndex === ActionIndexPath.Pickup)}
                />
              </Box>
            ) : (
              <Skeleton h={248} w="100%" />
            )}
            <Stack gap={rem(12)} pt={0} pb={rem(80 + 32)}>
              <Card>
                <Card.Section
                  withBorder
                  inheritPadding
                  py="xs"
                  style={{
                    '&[dataFirst]': {
                      borderColor: 'var(--mantine-color-neutral-0)',
                    },
                  }}
                >
                  <Title order={4}>
                    {tBooking(
                      pathIndex === ActionIndexPath.Pickup
                        ? 'location.pick_items_at'
                        : 'location.deliver_to'
                    )}
                  </Title>
                </Card.Section>
                <Stack mt="sm">
                  {/* <Card p={0} radius="md" h={132} w="100%" ref={mapRef}>
                    {centerCoords ? (
                      <Image
                        src={`${publicEnv.NEXT_PUBLIC_MAP_URI}/styles/basic/static/${centerCoords?.longitude},${centerCoords?.latitude},15/${mapRef.current?.clientWidth || 375}x${132}.png?marker=${centerCoords?.longitude},${centerCoords?.latitude}|https://cdn-ahamoveimg.ahamove.com/map/${pathIndex === ActionIndexPath.Pickup ? 'pickup' : 'path'}-marker-focus.png|scale:1`}
                        alt={tBooking(
                          pathIndex === ActionIndexPath.Pickup
                            ? 'location.pick_items_at'
                            : 'location.deliver_to'
                        )}
                        fill
                      />
                    ) : (
                      <Skeleton h={132} w="100%" />
                    )}
                  </Card> */}

                  <Stack gap={4}>
                    <Group
                      wrap="nowrap"
                      justify="space-between"
                      align="flex-start"
                      w="100%"
                    >
                      <Text maw="calc(100% - 90px)" fw={700}>
                        {getValues('first_label')}
                      </Text>
                      <Button
                        flex="none"
                        size="compact-sm"
                        variant="light"
                        fw={700}
                        onClick={handleOpenAddressModal}
                      >
                        {tCommon('change')}
                      </Button>
                    </Group>
                    {selectedService?.delivery_type !==
                    DeliveryType.Nationwide ? (
                      <Text size="sm" fw={500} c="neutral.5">
                        {getValues('second_label')}
                      </Text>
                    ) : null}
                    {mainEntrance?.name ? (
                      <Group gap="2px">
                        <IconMinusCircle />
                        <Text size="sm" fw={500} c="neutral.5">
                          {mainEntrance.name}
                        </Text>
                      </Group>
                    ) : null}
                  </Stack>
                  {isTruckService ? (
                    <ScrollArea
                      h={50}
                      // className="w-[calc(100svw-32px)]"
                      scrollbars="x"
                    >
                      <Group gap="xs" w={600}>
                        <AddressLevel />
                      </Group>
                    </ScrollArea>
                  ) : null}

                  {isShowAdminAreas ? (
                    <PickAddressDetails onChangeAddress={setAddressEdited} />
                  ) : null}
                  <Controller
                    name="remarks"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FloatingLabelInput
                        label={tBooking('location.add_address_note')}
                        value={value}
                        onChange={(e) => onChange(e)}
                        h="auto"
                        // leftSection={<Remarks width={16} height={16} />}
                      />
                    )}
                  />
                </Stack>
              </Card>

              <PathRecipientInfo pathIndex={pathIndex} />

              {pathIndex !== ActionIndexPath.Pickup ? (
                isNationwide ? (
                  <NationwidePathCod
                    handleInputChange={handleInputChange}
                    handleOpenModal={handleOpenModal}
                    codInputRef={codInputRef}
                  />
                ) : (
                  <PathCod
                    currentPath={path}
                    handleInputChange={handleInputChange}
                    handleOpenModal={handleOpenModal}
                  />
                )
              ) : null}
              {pathIndex !== ActionIndexPath.Pickup && !isHideItemValue ? (
                <PathItemValue handleInputChange={handleInputChange} />
              ) : null}

              <Group
                justify="flex-end"
                mt="auto"
                gap={8}
                p="md"
                pos={{ base: 'fixed', sm: 'sticky' }}
                bottom={0}
                left={0}
                w="100%"
                bg="white"
                styles={{
                  root: {
                    zIndex: 100,
                    borderTop: isMobile
                      ? 'none'
                      : `1px solid var(--mantine-color-neutral-1)`,
                    boxShadow: isMobile ? shadow.buttonDock : 'none',
                  },
                }}
              >
                <Button
                  fullWidth
                  size="md"
                  h={rem(48)}
                  type="submit"
                  disabled={
                    !isValid ||
                    Boolean(errors.cod?.message) ||
                    mainEntrance?.enabled === false
                  }
                  loading={geocodeLoading}
                >
                  {tBooking('location.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </FormProvider>

        <Modal
          fullScreen
          closeOnEscape={false}
          zIndex={201}
          p={0}
          styles={{
            content: {
              backgroundColor: 'var(--mantine-color-neutral-0)',
            },
            body: { padding: 0 },
          }}
          closeOnClickOutside={false}
          opened={openedAutocomplete}
          onClose={handleCloseAddressModal}
          withCloseButton={false}
        >
          <ModalMapAutocomplete
            pathIndex={pathIndex}
            isPickup={Boolean(pathIndex === ActionIndexPath.Pickup)}
            path={path}
            onAddressChange={handleChangeAddress}
            isBackBtn={Boolean(selectedAddress)}
            onClose={handleCloseAddressModal}
          />
        </Modal>
      </>
    );

  return (
    <>
      <CustomComponent
        modalId={currentModalId}
        isOpen={isOpenCustomModal}
        onClose={() => handleCloseCustomModal(false)}
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
          },
        }}
        closeButtonProps={{
          pos: 'fixed',
          right: 24,
          top: 12,
        }}
      >
        {renderModalContent()}
      </CustomComponent>
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            width: '100%',
            position: 'relative',
            backgroundColor: 'var(--mantine-color-neutral-0)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            overflowX: 'hidden',
            borderRadius: rem(12),
            boxShadow: shadow.special,
            height: 'inherit',
          }}
        >
          <Group
            gap="xl"
            align="center"
            justify="space-between"
            w="100%"
            px="md"
            py="sm"
            pos="sticky"
            top={0}
            left={0}
            bg="white"
            styles={{
              root: {
                zIndex: 100,
                borderBottom: `1px solid var(--mantine-color-neutral-0)`,
              },
            }}
          >
            <Title order={4} flex={1} ta="left">
              {pathIndex === ActionIndexPath.Pickup
                ? tBooking('location.pickup_details')
                : tBooking('location.delivery_details')}
            </Title>
            <ActionIcon
              size={rem(24)}
              variant="transparent"
              color="neutral"
              c="neutral.7"
              onClick={handleCancel}
            >
              <Close width={24} height={24} />
            </ActionIcon>
          </Group>
          <Box
            style={{
              flexGrow: 1,
              flexShrink: 1,
              height: 'auto',
              maxHeight: rem(680 - 50 - 64),
              overflowY: 'auto',
            }}
          >
            <Stack gap={rem(8)} py="sm">
              <Card radius={0}>
                <Card.Section inheritPadding py="sm">
                  <Text fz={16} fw={700} lts={0.5} c="neutral.7">
                    {isBikeService
                      ? tBooking('location.location_note')
                      : tBooking('location.location_details')}
                    {isHMService ? (
                      <span style={{ color: 'red' }}> *</span>
                    ) : null}
                  </Text>
                </Card.Section>

                {isTruckService ? (
                  <Group gap={6} mb={12}>
                    <AddressLevel />
                  </Group>
                ) : null}

                <Stack gap={rem(8)}>
                  {isShowAdminAreas ? (
                    <PickAddressDetails onChangeAddress={setAddressEdited} />
                  ) : null}

                  <Controller
                    name="remarks"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FloatingLabelInput
                        label={tBooking('location.add_address_note')}
                        value={value}
                        onChange={(e) => onChange(e)}
                        h="auto"
                      />
                    )}
                  />

                  {isHMService &&
                  locationFeatures &&
                  locationFeatures.length > 0 ? (
                    <LocationFeaturesSelector features={locationFeatures} />
                  ) : null}
                </Stack>
              </Card>

              <PathRecipientInfo pathIndex={pathIndex} />

              {pathIndex !== ActionIndexPath.Pickup ? (
                isNationwide ? (
                  <NationwidePathCod
                    handleInputChange={handleInputChange}
                    handleOpenModal={handleOpenModal}
                    codInputRef={codInputRef}
                  />
                ) : (
                  <PathCod
                    handleInputChange={handleInputChange}
                    handleOpenModal={handleOpenModal}
                    currentPath={path}
                  />
                )
              ) : null}
              {pathIndex !== ActionIndexPath.Pickup && !isHideItemValue ? (
                <PathItemValue handleInputChange={handleInputChange} />
              ) : null}
            </Stack>
          </Box>
          <Group justify="flex-end" p={16} bg="white">
            <Button
              onClick={handleCancel}
              variant="subtle"
              color="red"
              size="md"
              fw={700}
              h={rem(48)}
            >
              {tBooking('location.cancel')}
            </Button>
            <Button
              size="md"
              fw={700}
              h={rem(48)}
              w={rem(120)}
              type="submit"
              variant="filled"
              disabled={!isValid || Boolean(errors.cod?.message)}
              loading={geocodeLoading}
            >
              {tBooking('location.save')}
            </Button>
          </Group>
        </form>
      </FormProvider>
    </>
  );
};

export default FormPath;
